import { useEffect, useRef, useState } from "react";
import { Frame } from "../headliner/DeckShell";

/* 05 The room runs on <mottaker> — typografi som grensesnitt, i deckets eget språk (papir, hårlinjer, Geist, mono for system).
   Én prompt skriver seg selv som stor display-tekst midt på arket → send → teksten trekker seg opp til et kicker-felt, en statuslinje
   går gjennom byggetrinnene, og resultatet — rangeringen på hovedskjermen i Grieghallen — tegner seg som en ledger: rad for rad,
   stemmer teller, hårlinjene fylles i merkevarefargen. Ingen «app-skjermbilde», ingen kort, ingen bobler.
   Klokkestyrt (rAF) m/ transitions, deterministisk; print = sluttbildet. */
export const PROMPT = "Build an app where the 700 leaders in Grieghallen submit their biggest AI question — and rank them live on the main screen.";
const CPS = 66;
const T0 = 700;
const T_TYPED = T0 + Math.round((PROMPT.length / CPS) * 1000);
const T_SENT = T_TYPED + 650;
const STEPS = ["Understanding the brief", "Data model — questions · votes · sessions", "Interface — submit · rank · main screen", "Deployed — askroom.norwegianaisummit.no"];
const S0 = T_SENT + 500, S_MS = 520;
const T_DEPLOYED = S0 + (STEPS.length - 1) * S_MS;
const T_HEAD = T_SENT + 900;                    /* ledger-hode + hårlinje tegnes */
const R0 = T_HEAD + 500, R_MS = 210;             /* rader */
const QUESTIONS = [
  ["How do we deploy AI in a regulated business without slowing down?", "CEO · Finance", 214],
  ["Where does the energy for all this compute come from?", "Board member · Energy", 187],
  ["What does the board need to understand before we sign?", "Chair · Industry", 163],
  ["Which jobs change first — and who retrains them?", "CHRO · Public sector", 136],
  ["How do we keep our data — and our models — in Norway?", "CIO · Health", 118],
];
const T_COUNT = R0 + QUESTIONS.length * R_MS + 200;
const COUNT_MS = 1400;
const T_LIVE = T_COUNT + 300;
export const T_END = T_COUNT + COUNT_MS + 700;

const ease = (x) => 1 - Math.pow(1 - Math.min(1, Math.max(0, x)), 3);
const cnt = (t, from, ms = COUNT_MS) => (t >= from ? ease((t - from) / ms) : 0);

const useClock = (print) => {
  const [t, setT] = useState(print ? T_END : 0);
  const [run, setRun] = useState(0);
  const start = useRef(0);
  useEffect(() => {
    if (print) return undefined;
    let raf;
    start.current = performance.now();
    setT(0);
    const tick = (now) => {
      const el = now - start.current;
      setT(el);
      if (el < T_END) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [print, run]);
  return [t, () => setRun((r) => r + 1)];
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12h14M13 5l7 7-7 7" /></svg>
);

export const BuildSequence = ({ nr, mottaker, print }) => {
  const [t, replay] = useClock(print);
  const typed = PROMPT.slice(0, Math.max(0, Math.floor(((t - T0) / 1000) * CPS)));
  const ready = t >= T_TYPED;
  const sent = t >= T_SENT;
  const done = t >= T_END;
  const name = mottaker || "the Build Partner";
  const step = Math.min(STEPS.length - 1, Math.max(0, Math.floor((t - S0) / S_MS)));
  const max = QUESTIONS[0][2];
  const joined = Math.round(684 * cnt(t, T_LIVE, 1600));
  const state = ["bs", (print || t >= 60) && "live", ready && "ready", sent && "sent", t >= T_HEAD && "head", t >= T_DEPLOYED && "deployed", t >= T_LIVE && "on-air", done && "done"].filter(Boolean).join(" ");
  return (
    <Frame nr={nr} label={`The room runs on ${name}`} mottaker={mottaker} className={state}>
      <div className="bs-prompt" data-testid="bs-prompt">
        <span className="k" data-testid="bs-kick">Prompt<em> · built on {name} · before the day begins</em></span>
        <p className="t" data-testid="bs-prompt-text">{typed}{!sent && <i className="cur" aria-hidden />}</p>
        <i className="line" aria-hidden />
        <span className="send" aria-hidden><Arrow /></span>
      </div>

      <p className="bs-status" data-testid="bs-status" aria-hidden>
        <i /><span key={step}>{t >= S0 ? STEPS[step] : ""}</span>
      </p>

      <div className="bs-screen" data-testid="bs-app" aria-hidden>
        <div className="hd">
          <span className="a"><b>Ask the room</b> · main screen · Grieghallen</span>
          <span className="b" data-testid="bs-live"><i />{t >= T_LIVE ? `Live · ${joined} of 700 in the room` : "askroom.norwegianaisummit.no"}</span>
        </div>
        <ol className="rows">
          {QUESTIONS.map(([q, tag, v], i) => {
            const p = cnt(t, T_COUNT + i * 120);
            return (
              <li key={q} className={t >= R0 + i * R_MS ? "on" : ""}>
                <span className="n">0{i + 1}</span>
                <span className="q">{q}<em>{tag}</em></span>
                <span className="v">{t >= T_COUNT ? Math.round(v * p) : ""}</span>
                <i className="bar" style={{ "--p": (v / max) * p }} />
              </li>
            );
          })}
        </ol>
      </div>

      <p className="bd-foot bs-foot" data-testid="bs-state"><b>The room runs on {name}.</b> The summit's audience layer — questions and voting, build ideas, agenda, interaction, a personal recap afterwards — is built on {name}. Before the live build even begins, 700 people are already using it. The live build on the main stage is the real business case.</p>
      {!print && <button type="button" className="bs-replay" onClick={(e) => { e.stopPropagation(); replay(); }} data-testid="bs-replay">Replay</button>}
    </Frame>
  );
};
